import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Button,
  createStyles,
  Group,
  Tabs,
  Stack,
  Title,
  Accordion,
  Textarea,
  TextInput,
  Select,
  Badge,
  Text,
  Loader,
  Box
} from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { select as d3Select } from "d3";
import cloud from "d3-cloud";
import tinycolor from "tinycolor2";
import showdown from "showdown";

import Highlight from "../../../spaces_neo/presentations/discussion/HighlightCard";
import Avatar from "../../../utils/LazyAvatar";
import DefaultAvatar from "../../../../src/images/fallback/default.png";

import MessageModel from "../../../../models/Message";
import MediaFileModel from "../../../../models/MediaFile";
import EngagementFunctionModel from "../../../../models/EngagementFunction";

import isDark from "../../../../utils/IsDarkColor";
import isSpaceThemed from "../../../../utils/IsSpaceThemed";

const useStyles = createStyles((theme, { colors }) => ({
  tabControl: {
    color: `${colors.default_text_color} !important`,
    "&:hover": {
      backgroundColor: `${
        isDark(colors.sidebar_background_color)
          ? tinycolor(colors.sidebar_background_color).lighten(10).toString()
          : tinycolor(colors.sidebar_background_color).darken(10).toString()
      } !important`,
      color: `${colors.sidebar_text_color} !important`,
    },
  },
  tabActive: {
    backgroundColor: `${
      isSpaceThemed(colors)
        ? colors.sidebar_background_color
        : tinycolor(colors.sidebar_background_color).darken(5).toString()
    } !important`,
    color: `${colors.sidebar_text_color} !important`,
    "&:hover": {
      backgroundColor: `${colors.sidebar_background_color} !important`,
      color: `${colors.sidebar_text_color} !important`
    },
  },
  tabsListWrapper: {
    borderBottom: isSpaceThemed(colors)
      ? `1px solid ${
          isDark(colors.background_color)
            ? tinycolor(colors.background_color).lighten(20).toString()
            : tinycolor(colors.background_color).darken(20).toString()
        } !important`
      : `1px solid #e9ecef !important`,
  },
  badgeClass: {
    backgroundColor: colors.default_badge_color,
    color: colors.default_badge_text_color,
    height: 20,
    padding: "0 4px",
  },
  summaryCard: {
    color: isSpaceThemed(colors) ? colors.default_text_color : "#000",
    backgroundColor: isSpaceThemed(colors)
      ? isDark(colors.background_color)
        ? tinycolor(colors.background_color).lighten(5).toString()
        : tinycolor(colors.background_color).darken(5).toString()
      : tinycolor(colors.background_color).darken(2).toString(),
    borderLeft: isSpaceThemed(colors)
      ? `6px solid ${
          isDark(colors.background_color)
            ? tinycolor(colors.background_color).lighten(20).toString()
            : tinycolor(colors.background_color).darken(20).toString()
        }`
      : `1px solid #e9ecef`,
    padding: "1rem",
    marginBottom: "2rem",
    fontStyle: "italic",
    borderRadius: "6px",
  },
  themes: {
    marginTop: "2rem",
  },
  accordionDetails: {
    color: isSpaceThemed(colors) ? colors.default_text_color : "#000",
    backgroundColor: isSpaceThemed(colors)
      ? isDark(colors.background_color)
        ? tinycolor(colors.background_color).lighten(5).toString()
        : tinycolor(colors.background_color).darken(5).toString()
      : tinycolor(colors.background_color).darken(2).toString(),
    border: isSpaceThemed(colors)
      ? `1px solid ${
          isDark(colors.background_color)
            ? tinycolor(colors.background_color).lighten(20).toString()
            : tinycolor(colors.background_color).darken(20).toString()
        }`
      : `1px solid #e9ecef`,
    borderRadius: "6px",
    padding: "1rem",
    marginBottom: "1rem",
  },
  accordionSummary: {
    fontWeight: "bold",
    fontSize: "1.1rem",
    cursor: "pointer",
    outline: "none",
    padding: "0.5rem 0",
  },
  quoteBox: {
    flex: 1,
    cursor: "pointer",
    margin: "1rem 0",
    padding: "0.75rem",
    borderRadius: "6px",
    backgroundColor: isSpaceThemed(colors)
      ? isDark(colors.background_color)
        ? tinycolor(colors.background_color).lighten(10).toString()
        : tinycolor(colors.background_color).darken(10).toString()
      : tinycolor(colors.background_color).darken(2).toString(),
    borderLeft: isSpaceThemed(colors)
      ? `4px solid ${
          isDark(colors.background_color)
            ? tinycolor(colors.background_color).lighten(20).toString()
            : tinycolor(colors.background_color).darken(20).toString()
        }`
      : `1px solid #e9ecef`,
  }
}));

const QuoteBox = ({ type, quote, onClickQuote, classes, avatar_url }) => (
  <Group spacing={15} noWrap>
    {type === "quote" && <Avatar src={avatar_url || DefaultAvatar} size={50} radius="xl" />}
    <Box className={classes.quoteBox} onClick={() => onClickQuote(quote)}>
      <Text size="sm" sx={{ flex: 1 }}>"{quote.text}"</Text>
    </Box>
  </Group>
);

const QuotesSection = ({ quotes, onClickQuote, classes, avatars }) => {
  if (!quotes || !Array.isArray(quotes) || quotes.length === 0) {
    return null;
  }

  return (
    <>
      {quotes.map((quote, quoteIndex) => {
        const message = avatars.find((item) => item.id === quote.id);
        return (
          <QuoteBox
            key={quoteIndex}
            type="quote"
            quote={quote}
            onClickQuote={onClickQuote}
            classes={classes}
            avatar_url={message?.avatar?.url}
          />
        );
      })}
    </>
  );
};

const ThemeItemWithDetails = ({ item, onClickQuote, classes, hideTitle = false, avatars }) => (
  <div style={{ marginBottom: '1rem' }}>
    {!hideTitle && item.title && (
      <Text size="sm" mb="md" weight={500}>
        {item.title}
      </Text>
    )}
    {item.description && (
      <Text size="sm" mb="md" style={{ fontStyle: 'italic' }}>
        {item.description}
      </Text>
    )}
    {item.text && (
      <Text size="sm" mb="md">
        {item.text}
      </Text>
    )}
    {item.question ? (
      <QuoteBox
        type="question"
        quote={{ text: item.question }} 
        onClickQuote={() => {}} 
        classes={classes} 
      />
    ) : (
      <QuotesSection quotes={item.quotes} onClickQuote={onClickQuote} classes={classes} avatars={avatars} />
    )}
  </div>
);

const SummarySection = ({ summary, classes }) => {
  if (!summary) return null;
  
  return (
    <div className={classes.summaryCard}>
      <Text>{summary}</Text>
    </div>
  );
};

const SectionTitle = ({ title, classes }) => (
  <Text style={{ fontWeight: 'bold', fontSize: '1.2rem', marginBottom: '1rem' }}>
    {title.charAt(0).toUpperCase() + title.slice(1)}
  </Text>
);

const AccordionItem = ({ title, children, classes }) => (
  <div className={classes.accordionDetails}>
    <details>
      <summary className={classes.accordionSummary}>
        {title.charAt(0).toUpperCase() + title.slice(1)}
      </summary>
      {children}
    </details>
  </div>
);

const DefaultLayout = ({ summary, onClickQuote, classes, avatars }) => {
  if (!summary || typeof summary !== 'object') return null;

  return (
    <div style={{ marginTop: "20px" }}>
      <SummarySection summary={summary.summary} classes={classes} />
      {Object.keys(summary).map((sectionKey) => {
        if (sectionKey === 'summary') return null;
        
        const sectionData = summary[sectionKey];
        if (Array.isArray(sectionData)) {
          return (
            <div key={sectionKey} className={classes.themes}>
              {sectionData.map((theme, index) => (
                <AccordionItem key={`${sectionKey}_${index}`} title={theme.title || `Theme ${index + 1}`} classes={classes}>
                  <ThemeItemWithDetails 
                    item={theme} 
                    onClickQuote={onClickQuote} 
                    classes={classes} 
                    hideTitle={true}
                    avatars={avatars}
                  />
                </AccordionItem>
              ))}
            </div>
          );
        }
        return null;
      })}
    </div>
  );
};

const AccordionLayout = ({ summary, onClickQuote, classes, avatars }) => {
  if (!summary || typeof summary !== 'object') return null;

  return (
    <div style={{ marginTop: "20px" }}>
      <SummarySection summary={summary.summary} classes={classes} />
      {Object.keys(summary).map((sectionKey) => {
        if (sectionKey === 'summary') return null;
        
        const sectionData = summary[sectionKey];
        if (typeof sectionData === 'object' && sectionData !== null) {
          return (
            <div key={sectionKey} className={classes.themes}>
              {Object.keys(sectionData).map((subsectionKey) => {
                const subsectionData = sectionData[subsectionKey];
                if (Array.isArray(subsectionData)) {
                  return (
                    <div key={`${sectionKey}_${subsectionKey}`}>
                      <SectionTitle title={subsectionKey} classes={classes} />
                      {subsectionData.map((item, index) => (
                        <AccordionItem key={`${subsectionKey}_${index}`} title={item.title || `Item ${index + 1}`} classes={classes}>
                          <ThemeItemWithDetails 
                            item={item} 
                            onClickQuote={onClickQuote} 
                            classes={classes} 
                            hideTitle={true}
                            avatars={avatars}
                          />
                        </AccordionItem>
                      ))}
                    </div>
                  );
                }
                return null;
              })}
            </div>
          );
        }
        return null;
      })}
    </div>
  );
};

const TextLayout = ({ summary, onClickQuote, classes, avatars }) => {
  if (!summary || typeof summary !== 'object') return null;

  return (
    <div style={{ marginTop: "20px" }}>
      {Object.keys(summary).map((sectionKey) => {
        const sectionData = summary[sectionKey];
        if (Array.isArray(sectionData)) {
          return (
            <div key={sectionKey}>
              {sectionData.map((item, index) => (
                <div key={`${sectionKey}_${index}`} style={{ marginBottom: '1rem' }}>
                  <ThemeItemWithDetails 
                    item={item}
                    onClickQuote={onClickQuote}
                    classes={classes}
                    avatars={avatars}
                  />
                </div>
              ))}
            </div>
          );
        }
        return null;
      })}
    </div>
  );
};

const AccordionHeadingLayout = ({ summary, onClickQuote, classes, avatars }) => {
  if (!summary || typeof summary !== 'object') return null;

  return (
    <div style={{ marginTop: "20px" }}>
      <SummarySection summary={summary.summary} classes={classes} />
      {Object.keys(summary).map((sectionKey) => {
        if (sectionKey === 'summary') return null;
        
        const sectionData = summary[sectionKey];
        if (typeof sectionData === 'object' && sectionData !== null) {
          return (
            <div key={sectionKey} className={classes.themes}>
              <SectionTitle title={sectionKey} classes={classes} />
              {Object.keys(sectionData).map((subsectionKey) => {
                const subsectionData = sectionData[subsectionKey];
                if (Array.isArray(subsectionData) && subsectionData.length > 0) {
                  return (
                    <AccordionItem key={`${sectionKey}_${subsectionKey}`} title={subsectionKey} classes={classes}>
                      {subsectionData.map((item, index) => (
                        <div key={`${subsectionKey}_${index}`}>
                          <ThemeItemWithDetails 
                            item={item}
                            onClickQuote={onClickQuote} 
                            classes={classes} 
                            hideTitle={true}
                            avatars={avatars}
                          />
                        </div>
                      ))}
                    </AccordionItem>
                  );
                }
                return null;
              })}
            </div>
          );
        }
        return null;
      })}
    </div>
  );
};

const LayoutDetector = ({ summary, onClickQuote, classes, avatars }) => {
  if (!summary || typeof summary !== 'object') return null;

  if (summary.layout) {
    switch (summary.layout) {
      case "accordion":
        return <AccordionLayout summary={summary} onClickQuote={onClickQuote} classes={classes} avatars={avatars} />;
      case "text":
        return <TextLayout summary={summary} onClickQuote={onClickQuote} classes={classes} avatars={avatars} />;
      case "accordion_heading":
        return <AccordionHeadingLayout summary={summary} onClickQuote={onClickQuote} classes={classes} avatars={avatars} />;
      default:
        return <DefaultLayout summary={summary} onClickQuote={onClickQuote} classes={classes} avatars={avatars} />;
    }
  }

  if (summary.summary && Object.keys(summary).some(key => key !== 'summary' && Array.isArray(summary[key]))) {
    return <DefaultLayout summary={summary} onClickQuote={onClickQuote} classes={classes} avatars={avatars} />;
  }

  if (Object.keys(summary).some(key => Array.isArray(summary[key]) && summary[key].some(item => item.text))) {
    return <TextLayout summary={summary} onClickQuote={onClickQuote} classes={classes} avatars={avatars} />;
  }

  if (Object.keys(summary).some(key => key !== 'summary' && typeof summary[key] === 'object' && 
      Object.keys(summary[key]).some(subKey => Array.isArray(summary[key][subKey])))) {
    return <AccordionHeadingLayout summary={summary} onClickQuote={onClickQuote} classes={classes} avatars={avatars} />;
  }

  if (summary.summary && Object.keys(summary).some(key => key !== 'summary' && typeof summary[key] === 'object')) {
    return <AccordionLayout summary={summary} onClickQuote={onClickQuote} classes={classes} avatars={avatars} />;
  }

  return null;
};

const SelectItem = ({ label, description, ...others }) => (
  <div {...others}>
    <Group noWrap spacing={0}>
      <div>
        <Text size="sm" weight={600}>{label}</Text>
        <Text size="xs" color="dimmed">{description}</Text>
      </div>
    </Group>
  </div>
);

export default function InteractiveTabs({
  node,
  editor,
  swiperRef,
  space,
  aiSummary,
  activeTab,
  onChangeTab,
  activityCount,
  dummyPostsPrompt,
  setDummyPostsPrompt,
  functions,
  oriFunctions,
  onOpenReflections,
  onUpdatedMessage,
  tagging,
  summaries,
  setTagging,
  isManager,
  onMoveOn,
  onFetch
}) {
  const colors = useSelector(state => state.colors);
  const { classes } = useStyles({ colors });

  const [selectedFunction, setSelectedFunction] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);
  const [dummyPostsEmail, setDummyPostsEmail] = useState("");
  const [triggerActionsEmail, setTriggerActionsEmail] = useState("");
  const [selectedSummary, setSelectedSummary] = useState("general");
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const [generateType, setGenerateType] = useState(null);
  const [canGenerateSummary, setCanGenerateSummary] = useState(false);
  const [avatars, setAvatars] = useState([]);
  const [summaryCheckCompleted, setSummaryCheckCompleted] = useState(false);

  const highlights = Array.isArray(aiSummary?.highlights) ? aiSummary.highlights : [];
  const _summaries = Array.isArray(summaries) ? summaries.map((s) => ({ 
    ...s, 
    value: s.key,
    layout: s.layout || "accordion_heading"
  })) : [];

  const ai_summaries_by_type = aiSummary?.ai_summaries_by_type ? aiSummary.ai_summaries_by_type : {};
  const selectedSummaryData = ai_summaries_by_type[selectedSummary];
  const _summary = selectedSummaryData?.summary || (selectedSummary === "general" ? aiSummary?.summary : null);
  
  const summaryWithLayout = _summary && typeof _summary === 'object' ? {
    ..._summary,
    layout: selectedSummaryData?.layout || _summaries.find(s => s.key === selectedSummary)?.layout || "accordion_heading"
  } : _summary;

  const layout = useRef();

  useEffect(() => {
    if (aiSummary) {
      setTimeout(() => onFetchAvatars(aiSummary), 1000);
    }
  }, [aiSummary]);

  useEffect(() => {
    if (activeTab === 1 && aiSummary && _summaries && !summaryCheckCompleted) {
      checkAndGenerateMissingSummaries();
      setSummaryCheckCompleted(true);
    }
  }, [activeTab, aiSummary, _summaries, summaryCheckCompleted]);

  const onHighlightsTrigger = () => {
    MessageModel.onAiSummaryTrigger(
      node.attrs.id,
      (data) => alert("summary triggered"),
      (data) => {}
    );
  };

  const onContextTrigger = () => {
    MessageModel.onGenerateContext(
      node.attrs.id,
      (data) => alert("context triggered"),
      (data) => {}
    );
  };

  const onTranscriptsTrigger = () => {
    const doc = editor.view.state.doc;

    doc.nodesBetween(0, doc.content.size, (_node, pos) => {
      if (_node.type.name == "embed") {
        const mediaFile = {
          original_url: _node.attrs.src,
          url: _node.attrs.src,
          upload_type: "embed",
          slug: window.$currentSlug,
          embed_id: _node.attrs.videoId,
          apify: true,
        };

        MediaFileModel.onCreate(
          mediaFile,
          (data) => alert("MEDIA FILE STORED"),
          (data) => {}
        );
      }
    });
  };

  const onSuggestedPrompt = () => {
    MessageModel.onSuggestedPrompt(
      node.attrs.id,
      (data) => alert("prompt triggered"),
      (data) => {}
    );
  };

  const onDummyPosts = () => {
    MessageModel.onDummyPosts(
      node.attrs.id,
      dummyPostsEmail,
      dummyPostsPrompt,
      (data) => alert("dummy post generated"),
      (data) => {}
    );
  };

  const onTriggerActions = () => {
    MessageModel.onTriggerActions(
      node.attrs.id,
      (data) => alert(`trigger action for ${triggerActionsEmail}`),
      (data) => {}
    );
  };

  const onTriggerDispatcher = () => {
    MessageModel.onTriggerDispatcher(
      node.attrs.id,
      triggerActionsEmail,
      (data) => alert(`trigger action for ${triggerActionsEmail}`),
      (data) => {}
    );
  };

  const onTriggerAction = () => {
    MessageModel.onTriggerAction(
      node.attrs.id,
      triggerActionsEmail,
      selectedAction,
      (data) => alert(`trigger action`),
      (data) => {}
    );
  };

  const onGetMemberActions = () => {
    MessageModel.onGetMemberActions(
      node.attrs.id,
      triggerActionsEmail,
      (data) => alert(data.actions),
      (data) => {}
    );
  };

  const onGenerateSummary = (summary_type) => {
    const summaryData = aiSummary?.ai_summaries_by_type[summary_type];
    if (summaryData?.last_updated && !canGenerateSummary) {
      const diffInMs = new Date() - new Date(summaryData.last_updated);
      const diffInHours = diffInMs / (1000 * 60 * 60);
      if (diffInHours < 12) {
        return;
      }
    }

    setGenerateType(summary_type);

    const summary_space_slug = _summaries.find((s) => s.key === summary_type)?.summary_space_slug;

    if (!summary_space_slug) return;

    EngagementFunctionModel.onGenerateSummary(
      { space_id: space.id, summary_space_slug, summary_type },
      (data) => {
        if (typeof onFetch === "function") {
          onFetch();
        }
        setTimeout(() => setGenerateType(null), 1000);
      },
      (err) => setGenerateType(null)
    );
  };

  const onFetchAvatars = (data) => {
    if (!data?.ai_summaries_by_type) return;
    
    const messageIds = extractMessageIds(data?.ai_summaries_by_type);
    
    if (messageIds.length === 0) {
      return;
    }
    
    MessageModel.onFetchAvatars(
      { message_ids: messageIds },
      (data) => {
        if (Array.isArray(data)) {
          setAvatars(data);
        }
      },
      (err) => console.log("Error fetching avatars:", err)
    );
  };

  const drawWordcloud = () => {
    if (Array.isArray(aiSummary?.wordcloud) && aiSummary.wordcloud.length > 0) {
      setTimeout(() => {
        const sortedWordcloud = aiSummary.wordcloud.sort(
          (a, b) => b.frequency - a.frequency
        );
        const maxValue = sortedWordcloud[0].frequency;
        const array = aiSummary.wordcloud.map(function (d) {
          const max = 85;
          const min = 15;

          let size = max * (d.frequency / maxValue);

          if (size < min) {
            size = min;
          }
          return { text: d.word, size: size };
        });

        const element = document.getElementById(`wc-canvas-${node.attrs.id}`);

        if (!element) return;

        layout.current = cloud()
          .size([element.offsetWidth, 400])
          .words(array)
          .padding(5)
          .rotate(function () {
            return (~~(Math.random() * 6) - 3) * 30;
          })
          .font("Impact")
          .fontSize(function (d) {
            return d.size;
          })
          .on("end", draw)
          .spiral("archimedean");

        layout.current.start();
      }, 100);
    }
  };

  const draw = (words) => {
    const ele = d3Select(`#wc-canvas-${node.attrs.id}`)
      .append("svg")
      .attr("width", layout.current.size()[0])
      .attr("height", layout.current.size()[1])
      .append("g")
      .attr(
        "transform",
        "translate(" +
          layout.current.size()[0] / 2 +
          "," +
          layout.current.size()[1] / 2 +
          ")"
      )
      .selectAll("text")
      .data(words)
      .enter()
      .append("text")
      .style("font-size", function (d) {
        return d.size + "px";
      })
      .style("font-family", "Impact")
      .style("fill", function (d) {
        return randomColor();
      })
      .attr("text-anchor", "middle")
      .attr("transform", function (d) {
        return "translate(" + [d.x, d.y] + ")rotate(" + d.rotate + ")";
      })
      .text(function (d) {
        return d.text;
      });
  };

  const randomColor = () => {
    const pastelColors = [
      "#9b59b6", // Dark pastel purple
      "#34495e", // Dark pastel blue
      "#16a085", // Dark pastel green
      "#8e44ad", // Dark pastel violet
      "#2c3e50", // Dark pastel gray
      "#27ae60", // Dark pastel green
      "#2980b9", // Dark pastel blue
      "#c0392b", // Dark pastel red
      "#e74c3c", // Dark pastel pink
      "#ecf0f1", // Dark pastel gray
      "#f39c12", // Dark pastel orange
      "#d35400", // Dark pastel orange
      "#bdc3c7", // Dark pastel gray
      "#7f8c8d", // Dark pastel gray
      "#1abc9c", // Dark pastel green
      "#3498db", // Dark pastel blue
      "#95a5a6", // Dark pastel gray
      "#e67e22", // Dark pastel orange
      "#e74c3c", // Dark pastel red
      "#bdc3c7", // Dark pastel gray
    ];
    const randomIndex = Math.floor(Math.random() * pastelColors.length);
    return pastelColors[randomIndex];
  };

  const checkAndGenerateMissingSummaries = () => {
    if (!aiSummary?.ai_summaries_by_type || !_summaries) return;
    
    const availableSummaryTypes = _summaries.map(s => s.key).filter(key => key !== 'wordcloud');
    
    const missingSummaries = availableSummaryTypes.filter(summaryType => {
      const summaryData = aiSummary.ai_summaries_by_type[summaryType];
      return !summaryData?.last_updated;
    });

    missingSummaries.forEach(summaryType => {
      onGenerateSummary(summaryType);
    });
  };

  const onChangeSummary = (value) => {
    setSelectedSummary(value);
    if (value === "wordcloud") {
      drawWordcloud();
    } else {
      onGenerateSummary(value);
    }
  };

  const onCopySummary = () => {
    setCopiedToClipboard(true);
    setTimeout(() => {
      setCopiedToClipboard(false);
    }, 2000);
  };

  const onClickQuote = (quote) => {
    if (typeof onOpenReflections === "function" && quote?.id) {
      onOpenReflections(quote.id);
    }
  };

  const onSummaryHTML = () => {
    const converter = new showdown.Converter();
    return converter.makeHtml(summaryWithLayout);
  };

  const extractMessageIds = (data) => {
    const messageIds = new Set();
    
    const traverse = (obj) => {
      if (!obj || typeof obj !== 'object') return;
      
      if (Array.isArray(obj)) {
        obj.forEach(item => traverse(item));
        return;
      }
      
      if (obj.id && typeof obj.id === 'number') {
        messageIds.add(obj.id);
      }
      
      if (obj.quotes && Array.isArray(obj.quotes)) {
        obj.quotes.forEach(quote => traverse(quote));
      }
      
      Object.values(obj).forEach(value => {
        if (typeof value === 'object' && value !== null) {
          traverse(value);
        }
      });
    };
    
    traverse(data);
    return Array.from(messageIds);
  };

  return (
    <Tabs
      active={activeTab}
      onTabChange={(newTab) => {
        onChangeTab(newTab);
        if (selectedSummary === "wordcloud") {
          setSelectedSummary("general");
        }
      }}
      variant="pills"
      position="center"
      styles={{
        tabControl: classes.tabControl,
        tabActive: classes.tabActive
      }}
    >
      <Tabs.Tab label="Highlights">
        <Swiper
          onSwiper={(swiper) => { swiperRef.current = swiper }}
          slidesPerView={"auto"}
          centeredSlides={true}
          navigation={{ nextEl: ".custom-next", prevEl: ".custom-prev" }}
          spaceBetween={20}
          modules={[Navigation]}
          className="mySwiper"
        >
          {highlights.map((message, index) => {
            return (
              <SwiperSlide>
                {({ isActive }) => (
                  <Highlight
                    key={`message_${message.id}`}
                    spaceId={space.slug}
                    spaceVersion={space.version}
                    messageId={message.id}
                    htmlId={`${message.type}_${message.id}`}
                    messageCreatedAt={message.created_at}
                    messageTitle={message?.post?.subject || null}
                    messageContent={
                      message.version == "new"
                        ? message.content
                        : message.message_body
                    }
                    messageUnseenCount={message.unseen_count}
                    tags={message.tags}
                    reflections={message.reflections}
                    user={message.user ? message.user : {}}
                    onOpenReflections={onOpenReflections}
                    version={message.version}
                    canPin={false}
                    canTag={message?.user?.can_tag || false}
                    canLike={message?.user?.can_like || false}
                    canReflect={message.user.can_reflect}
                    canEdit={message?.user?.can_edit || false}
                    canDelete={message?.user?.can_delete || false}
                    onUpdated={onUpdatedMessage}
                    type={"pod_message"}
                    tagging={tagging}
                    setTagging={setTagging}
                    isPinned={space.pin_message == message.id}
                    canSetOpen={message.can_set_open}
                    canSetPrivate={message.can_set_private}
                    isManager={isManager}
                    isPrivate={message.is_private}
                    text={message.text}
                    isLiked={message.is_liked}
                    likesCount={message.likes_count}
                    highlightIndex={index}
                    onMoveOn={onMoveOn}
                    currentSlideActive={isActive}
                    isLastIndex={index == highlights.length - 1}
                  />
                )}
              </SwiperSlide>
            );
          })}

          <div className="custom-prev"></div>
          <div className="custom-next"></div>
        </Swiper>
      </Tabs.Tab>

      <Tabs.Tab label="Summary">
        <Group spacing={10} position="center">
          <Select
            value={selectedSummary}
            placeholder="Select summary"
            radius="xl"
            maxDropdownHeight={400}
            data={[..._summaries, { label: "Wordcloud", value: "wordcloud", description: "Most mentioned words from responses." }]}
            itemComponent={SelectItem}
            onChange={onChangeSummary}
          />
          {selectedSummary !== "wordcloud" && typeof summaryWithLayout === "string" && summaryWithLayout != "none" && highlights.length > 1 && (
            <CopyToClipboard
              text={summaryWithLayout}
              onCopy={onCopySummary}
            >
              <Group
                py={5}
                px={15}
                sx={(theme) => ({
                  cursor: "pointer",
                  borderRadius: 30,
                  color: colors.primary_button_background_color,
                  border: `1px solid ${colors.primary_button_background_color}`
                })}
              >
                <FontAwesomeIcon icon={copiedToClipboard ? solid("check") : solid("file-alt")} color={colors.primary_button_background_color} />
                <Text color={colors.primary_button_background_color} size="sm" weight={500}>
                  {copiedToClipboard ? "Summary copied" : "Copy summary"}
                </Text>
              </Group>
            </CopyToClipboard>
          )}
        </Group>
        {selectedSummary === "wordcloud" ? (
          <>
            {highlights.length <= 3 ? (
              <Text m={40} mt={30} color="dimmed" align="center">
                {"We’re still waiting for a few more responses to come in before we generate a wordcloud."}
              </Text>
            ) : (
              <div
                style={{ marginTop: "30px", marginBottom: "30px" }}
                id={`wc-canvas-${node.attrs.id}`}
              />
            )}
          </>
        ) : (
          <>
            {typeof generateType === "string" && selectedSummary === generateType && (
              <>
                <Group position="center" spacing={0} mt={30}>
                  <Loader color={colors.primary_button_background_color} />
                </Group>
                <Text mb={30} color="dimmed" align="center">{"Generating summary..."}</Text>
              </>
            )}
            {summaryWithLayout && summaryWithLayout != "none" && highlights.length > 1 ? (
              typeof summaryWithLayout === "string" ? (
                <div
                  className="markdown-cont"
                  dangerouslySetInnerHTML={{ __html: onSummaryHTML() }}
                />
              ) : (
                <LayoutDetector summary={summaryWithLayout} onClickQuote={onClickQuote} classes={classes} avatars={avatars} />
              )
            ) : !!generateType ? null : (
              <Text m={40} mt={30} color="dimmed" align="center">
                {highlights.length > 1
                  ? "We've received some responses! We're now generating a summary to give you the key insights shortly."
                  : "Waiting for more responses to generate a summary."
                }
              </Text>
            )}
          </>
        )}
      </Tabs.Tab>

      <Tabs.Tab
        label={
          <Group spacing={5} noWrap>
            {"Discussion"}
            {activityCount > 0 && (
              <Badge classNames={{ root: classes.badgeClass }}>
                {activityCount}
              </Badge>
            )}
          </Group>
        }
      />

      {window.$currentRole == "admin" && (
        <Tabs.Tab label="Admin">
          <Group mb={30} mt={20}>
            <Button radius="xl" onClick={() => onTranscriptsTrigger()}>
              {`Trigger Video Transcripts`}
            </Button>
            <Button radius="xl" onClick={() => onContextTrigger()}>
              {`Trigger Ai Context`}
            </Button>

            <Button radius="xl" onClick={() => onHighlightsTrigger()}>
              {`Trigger Ai Summary`}
            </Button>

            <Button radius="xl" onClick={() => onSuggestedPrompt()}>
              {`Trigger Suggested Prompt`}
            </Button>
            <Button radius="xl" onClick={() => setCanGenerateSummary(!canGenerateSummary)}>
              {`Turn ${canGenerateSummary ? "off" : "on"} summary generation`}
            </Button>
          </Group>
          <Stack>
            {aiSummary && (
              <Accordion multiple mb={50}>
                {aiSummary.prompt && (
                  <Accordion.Item label="Suggested prompt">
                    <Textarea minRows={6}>
                      {JSON.stringify(aiSummary.prompt)}
                    </Textarea>
                  </Accordion.Item>
                )}

                <Accordion.Item label="Context">
                  <Textarea minRows={6}>
                    {JSON.stringify(aiSummary.context)}
                  </Textarea>
                </Accordion.Item>

                <Accordion.Item label="Short Contexts">
                  <Title>{"Current"}</Title>
                  <Textarea minRows={6}>
                    {JSON.stringify(aiSummary.short_context)}
                  </Textarea>
                  <Title>{"Next"}</Title>
                  <Textarea minRows={6}>
                    {JSON.stringify(aiSummary.next_short_context)}
                  </Textarea>
                </Accordion.Item>

                <Accordion.Item label="Raw text">
                  <Textarea minRows={6}>
                    {aiSummary.raw_text}
                  </Textarea>
                </Accordion.Item>

                <Accordion.Item label="Summary">
                  <Textarea minRows={6}>
                    {JSON.stringify(aiSummary.summary)}
                  </Textarea>
                </Accordion.Item>

                <Accordion.Item label="Wordcloud">
                  {JSON.stringify(aiSummary.wordcloud)}
                </Accordion.Item>

                <Accordion.Item label="Highlights">
                  {JSON.stringify(aiSummary.highlights)}
                </Accordion.Item>

                <Accordion.Item label="Dummy posts">
                  <Stack>
                    <Textarea
                      minRows={6}
                      value={dummyPostsPrompt}
                      onChange={(event) => setDummyPostsPrompt(event.currentTarget.value)}
                    />
                    <TextInput
                      placeholder="Write an email"
                      label="Email of the author"
                      value={dummyPostsEmail}
                      onChange={(event) => setDummyPostsEmail(event.currentTarget.value)}
                    />
                    <Button radius={"xl"} onClick={() => onDummyPosts()}>
                      {"Create post"}
                    </Button>
                  </Stack>
                </Accordion.Item>

                <Accordion.Item label="Trigger actions">
                  <Stack>
                    <TextInput
                      placeholder="Write an email"
                      label="Email of the member"
                      value={triggerActionsEmail}
                      onChange={(event) => setTriggerActionsEmail(event.currentTarget.value)}
                    />
                    <Group>
                      <Button radius={"xl"} onClick={() => onTriggerActions()}>
                        {"Trigger actions"}
                      </Button>
                      <Button radius={"xl"} onClick={() => onGetMemberActions()}>
                        {"Get member actions"}
                      </Button>
                      <Button radius={"xl"} onClick={() => onTriggerDispatcher()}>
                        {"Trigger dispatcher"}
                      </Button>
                    </Group>
                  </Stack>
                </Accordion.Item>

                <Accordion.Item label="Trigger email">
                  <Stack>
                    <TextInput
                      placeholder="Write an email"
                      label="Email of the member"
                      value={triggerActionsEmail}
                      onChange={(event) => setTriggerActionsEmail(event.currentTarget.value)}
                    />
                    <Select
                      label={"Select trigger"}
                      value={selectedFunction}
                      onChange={setSelectedFunction}
                      placeholder={"Select trigger"}
                      data={functions}
                      maxDropdownHeight={400}
                      radius="sm"
                      size="md"
                    />
                    {selectedFunction && (
                      <Select
                        label={"Select action"}
                        value={selectedAction}
                        onChange={setSelectedAction}
                        placeholder={"Select action"}
                        data={
                          oriFunctions
                          .filter((item) => item.slug == selectedFunction)[0]
                          .actions.map((item) => ({
                            value: item.slug,
                            label: item.name,
                          }))
                        }
                        maxDropdownHeight={400}
                        radius="sm"
                        size="md"
                      />
                    )}
                    <Group>
                      <Button radius={"xl"} onClick={() => onTriggerAction()}>
                        {"Trigger action"}
                      </Button>
                    </Group>
                  </Stack>
                </Accordion.Item>
              </Accordion>
            )}
          </Stack>
        </Tabs.Tab>
      )}
    </Tabs>
  );
};